import { Box, Stack } from "@mui/material";
import { Flex } from "Components/_Shared/Core/Flex";
import { useSearchClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { SearchedClient, SearchedLocalClient } from "Infrastructure/ApiZfp/Schema";
import { useOnClickOutside } from "Infrastructure/Hooks/useOnClickOutside";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { debounce } from "lodash-es";
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { ClientsSearchInput } from "./ClientsSearchInput";
import { ClientsSearchResults } from "./ClientsSearchResults";

type Props = {
  onSelect: (client: SearchedClient) => void;
  onSelectLocal?: (client: SearchedLocalClient) => void;
  useFullWidth?: boolean;
  includeLocalClients?: boolean;
  showAnalysisStatus?: boolean;
};

export const ClientsSearch = ({
  onSelect,
  onSelectLocal,
  useFullWidth = true,
  includeLocalClients = false,
  showAnalysisStatus = false,
}: Props) => {
  const [existingClients, setExistingClients] = useState<SearchedClient[]>([]);
  const [localClients, setLocalClients] = useState<SearchedLocalClient[]>([]);
  const [showResults, setShowResults] = useState<boolean>(false);
  const { t } = useResource();

  const { mutate, isPending } = useSearchClients({
    mutation: {
      onSuccess: (data) => {
        setExistingClients(data.existingClients);
        setLocalClients(includeLocalClients ? data.localClients : []);
        setShowResults(true);
      },
      onError: (_) => {
        toast.error(t(Resources.Clients.SearchError));
      },
    },
  });

  const debouncedSearch = useCallback(
    debounce((phrase: string) => {
      mutate({ data: { phrase } });
    }, 300),
    [],
  );

  const handleFocus = () => {
    if (existingClients.length > 0 || localClients.length > 0) {
      setShowResults(true);
    }
  };

  const selectClient = (client: SearchedClient) => {
    onSelect(client);
    setShowResults(false);
  };

  const selectLocalClient = (client: SearchedLocalClient) => {
    onSelectLocal?.(client);
    setShowResults(false);
  };

  const ref = useRef<HTMLDivElement | null>(null);
  const handleOutsideClick = useCallback(() => {
    setShowResults(false);
  }, []);

  useOnClickOutside(ref, handleOutsideClick);

  return (
    <Flex width={"100%"} justifyContent={useFullWidth ? "center" : "flex-end"}>
      <Stack m={2} position={"relative"} spacing={1} sx={{ width: useFullWidth ? "600px" : "350px" }} borderRadius={2}>
        <Box ref={ref}>
          <ClientsSearchInput onSearch={debouncedSearch} onFocus={handleFocus} useFullWidth={useFullWidth} />
          {(isPending || showResults) && (
            <ClientsSearchResults
              isLoading={isPending}
              existingClients={existingClients}
              localClients={includeLocalClients ? localClients : undefined}
              onSelect={selectClient}
              onSelectLocal={selectLocalClient}
              showAnalysisStatus={showAnalysisStatus}
            />
          )}
        </Box>
      </Stack>
    </Flex>
  );
};
